import { useState } from "react";
import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useApiKeysQuery, useRevokeApiKeyMutation } from "@/features/apikeys/hooks/useApiKeys";

function formatDate(iso: string | null, locale: string, dash: string) {
  if (!iso) return dash;
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(iso),
  );
}

export function ApiKeysTable() {
  const { t, i18n } = useTranslation();
  const { data: apiKeys, isLoading } = useApiKeysQuery();
  const revoke = useRevokeApiKeyMutation();
  const [pendingRevokeId, setPendingRevokeId] = useState<string | null>(null);

  function handleConfirmRevoke() {
    if (!pendingRevokeId) return;
    revoke.mutate(pendingRevokeId);
    setPendingRevokeId(null);
  }

  if (isLoading) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{t("common.loading")}</p>;
  }

  if (!apiKeys || apiKeys.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{t("apiKeys.empty")}</p>;
  }

  return (
    <>
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-border text-xs text-muted-foreground">
            <th className="py-2 font-medium">{t("apiKeys.table.name")}</th>
            <th className="py-2 font-medium">{t("apiKeys.table.key")}</th>
            <th className="py-2 font-medium">{t("apiKeys.table.created")}</th>
            <th className="py-2 font-medium">{t("apiKeys.table.lastUsed")}</th>
            <th className="py-2 font-medium">{t("apiKeys.table.status")}</th>
            <th className="py-2" />
          </tr>
        </thead>
        <tbody>
          {apiKeys.map((key) => (
            <tr key={key.id} className="border-b border-border last:border-0">
              <td className="py-2.5">
                {key.name ?? (
                  <span className="italic text-muted-foreground">{t("apiKeys.unnamed")}</span>
                )}
              </td>
              <td className="py-2.5 font-mono text-xs">{key.keyPrefix}…</td>
              <td className="py-2.5 text-muted-foreground">
                {formatDate(key.createdAt, i18n.language, t("common.dash"))}
              </td>
              <td className="py-2.5 text-muted-foreground">
                {formatDate(key.lastUsedAt, i18n.language, t("common.dash"))}
              </td>
              <td className="py-2.5">
                {key.revoked ? (
                  <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
                    {t("apiKeys.revoked")}
                  </span>
                ) : (
                  <span className="rounded-full bg-run/10 px-2 py-0.5 text-xs text-run">
                    {t("apiKeys.active")}
                  </span>
                )}
              </td>
              <td className="py-2.5 text-right">
                {!key.revoked && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    disabled={revoke.isPending}
                    onClick={() => setPendingRevokeId(key.id)}
                    aria-label={t("apiKeys.revokeAria")}
                    title={t("apiKeys.revokeAria")}
                  >
                    <Trash2 />
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ConfirmDialog
        open={pendingRevokeId !== null}
        title={t("apiKeys.confirmRevokeTitle")}
        description={t("apiKeys.confirmRevokeDescription")}
        confirmLabel={t("apiKeys.revokeAria")}
        destructive
        onConfirm={handleConfirmRevoke}
        onCancel={() => setPendingRevokeId(null)}
      />
    </>
  );
}
